import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Warehouse } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Button from '../../components/ui/Button';
import Input from '../../components/forms/Input';
import Modal from '../../components/ui/Modal';
import AlmacenForm from './AlmacenForm';
import apiService from '../../services/apiService';
import './AlmacenesPage.css';

const AlmacenesPage = () => {
    const { loading, setLoading, setError, clearError } = useApp();

    const [almacenes, setAlmacenes] = useState([]);
    const [filteredAlmacenes, setFilteredAlmacenes] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedAlmacen, setSelectedAlmacen] = useState(null);

    useEffect(() => {
        loadAlmacenes();
    }, []);

    useEffect(() => {
        filterAlmacenes();
    }, [almacenes, searchQuery]);

    const loadAlmacenes = async () => {
        try {
            setLoading(true);
            clearError();

            const result = await apiService.getAlmacenes();

            if (result.success) {
                setAlmacenes(result.data);
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al cargar almacenes');
        } finally {
            setLoading(false);
        }
    };

    const filterAlmacenes = () => {
        if (!searchQuery.trim()) {
            setFilteredAlmacenes(almacenes);
            return;
        }

        const query = searchQuery.toLowerCase();
        const filtered = almacenes.filter(almacen =>
            almacen.nombre?.toLowerCase().includes(query) ||
            almacen.codigo?.toLowerCase().includes(query)
        );

        setFilteredAlmacenes(filtered);
    };

    const handleCreateAlmacen = async (almacenData) => {
        try {
            const result = await apiService.crearAlmacen(almacenData);

            if (result.success) {
                setShowCreateModal(false);
                loadAlmacenes();
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al crear almacén');
        }
    };

    const handleEditAlmacen = async (almacenData) => {
        try {
            const result = await apiService.actualizarAlmacen({
                id: selectedAlmacen.id,
                ...almacenData
            });

            if (result.success) {
                setShowEditModal(false);
                setSelectedAlmacen(null);
                loadAlmacenes();
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al actualizar almacén');
        }
    };

    const handleDeleteAlmacen = async (id, nombre) => {
        if (!window.confirm(`¿Estás seguro de que quieres desactivar el almacén "${nombre}"?\n\nNota: El almacén se ocultará de la lista pero se mantendrá en el historial.`)) {
            return;
        }

        try {
            const result = await apiService.eliminarAlmacen(id);

            if (result.success) {
                loadAlmacenes();
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al desactivar almacén');
        }
    };

    return (
        <div className="almacenes-page">
            {/* Controles */}
            <div className="almacenes-controls">
                <div className="search-filters">
                    <div className="search-container">
                        <Input
                            placeholder="Buscar por nombre o código..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            icon={<Search size={20} />}
                            className="search-input"
                        />
                    </div>
                </div>

                <div className="controls-actions">
                    <Button
                        onClick={() => setShowCreateModal(true)}
                        disabled={loading}
                    >

                        Nuevo Almacén
                    </Button>
                </div>
            </div>

            {/* Tabla de almacenes */}
            <div className="almacenes-table-container">
                {loading && (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Cargando almacenes...</p>
                    </div>
                )}

                {!loading && filteredAlmacenes.length === 0 && (
                    <div className="empty-state">
                        <Warehouse size={48} />
                        <h3>No se encontraron almacenes</h3>
                        <p>
                            {searchQuery
                                ? 'No hay almacenes que coincidan con tu búsqueda'
                                : 'No hay almacenes registrados en el sistema'
                            }
                        </p>
                        {!searchQuery && (
                            <Button
                                onClick={() => setShowCreateModal(true)}
                                className="empty-action-btn"
                            >

                                Crear primer almacén
                            </Button>
                        )}
                    </div>
                )}

                {!loading && filteredAlmacenes.length > 0 && (
                    <table className="almacenes-table">
                        <thead>
                            <tr>
                                <th>Almacén</th>
                                <th>Código</th>
                                <th>Estado</th>
                                <th data-type="actions">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredAlmacenes.map((almacen) => (
                                <tr key={almacen.id} className={almacen.activo ? '' : 'inactive-row'}>
                                    <td>
                                        <div className="almacen-name">{almacen.nombre}</div>
                                    </td>

                                    <td>
                                        <span className="codigo-badge">
                                            {almacen.codigo || 'Sin código'}
                                        </span>
                                    </td>

                                    <td>
                                        <span className={`status-dot ${almacen.activo ? 'active' : 'inactive'}`}>
                                            {almacen.activo ? '● Activo' : '● Inactivo'}
                                        </span>
                                    </td>

                                    <td>
                                        <div className="actions-compact">
                                            <button
                                                className="action-btn-compact"
                                                onClick={() => {
                                                    setSelectedAlmacen(almacen);
                                                    setShowEditModal(true);
                                                }}
                                                title="Editar"
                                            >
                                                <Edit size={14} />
                                            </button>

                                            <button
                                                className="action-btn-compact delete"
                                                onClick={() => handleDeleteAlmacen(almacen.id, almacen.nombre)}
                                                title="Desactivar"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modales */}
            {showCreateModal && (
                <Modal
                    title="Crear Nuevo Almacén"
                    onClose={() => setShowCreateModal(false)}
                    size="large"
                >
                    <AlmacenForm
                        onSubmit={handleCreateAlmacen}
                        onCancel={() => setShowCreateModal(false)}
                        isLoading={loading}
                    />
                </Modal>
            )}

            {showEditModal && selectedAlmacen && (
                <Modal
                    title="Editar Almacén"
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedAlmacen(null);
                    }}
                    size="large"
                >
                    <AlmacenForm
                        almacen={selectedAlmacen}
                        onSubmit={handleEditAlmacen}
                        onCancel={() => {
                            setShowEditModal(false);
                            setSelectedAlmacen(null);
                        }}
                        isLoading={loading}
                        isEdit={true}
                    />
                </Modal>
            )}
        </div>
    );
};

export default AlmacenesPage;
